import { LIGHTNING_STRIKE_COLOR, LIGHTNING_STRIKE_INNER_COLOR } from './colors';
import type { LightningGroup } from './types';

/**
 * SPAWN 1, authored in the lightning lab and pasted back.
 *
 * This is a RECORD OF WHAT WAS TUNED, not a generated look: the numbers came
 * off the lab's timeline by hand, so it is deliberately flat and explicit.
 * Nothing here is derived from anything else, which means a later round of
 * tuning can replace this file wholesale from a fresh "Copy config" export
 * without unpicking any factoring.
 *
 * DO NOT RENAME THE LINE IDS. `id` is not just a key -- index.tsx derives
 * each bolt's `lineSeed` from its characters, and that seed drives the jag
 * hash. A rename reshapes the bolt. That is also why the numbering skips
 * 1-2: that strand was cut, and the rest kept their names rather than being
 * renumbered into new shapes.
 *
 * Coordinates are relative to the group's origin, in engine widths; every
 * time is seconds at 1x, with `delay` measured from the group's spawn. The
 * group runs 1.05s end to end -- the last strands start at 0.55 and live 0.5.
 */
export const LIGHTNING_SPAWN_1_GROUP: LightningGroup = {
  id: 'spawn-1',
  name: 'Spawn 1',
  enabled: true,
  position: [0.54, 0.25, -0.81],
  rotationDeg: [12, -7, 21],
  scale: 2,
  lines: [
    // The opening strike: earliest, shortest-lived, and the only strand that
    // throws forward (+z) rather than back into the frame.
    {
      id: 'strand-1-1',
      enabled: true,
      start: [0, -0.07, 0],
      end: [0.72, 0.04, 0.67],
      delay: 0,
      lifetime: 0.37,
      revealSeconds: 0.07,
      brightness: 5.2,
      thickness: 0.0012,
      jag: 0.03,
      detail: 16,
      flickerHz: 10,
      color: LIGHTNING_STRIKE_COLOR,
      innerColor: LIGHTNING_STRIKE_INNER_COLOR,
      keyframes: [
        { time: 0, intensity: 1.8, endOffset: [0, 0, 0] },
        { time: 0.25, intensity: 1, endOffset: [0.18, 0.13, 0] },
      ],
    },
    // THE RESTRIKE PAIR, fired 0.05s apart from two origins so the flicker
    // reads as one forked bolt. They now END OPPOSITELY, which is what stops
    // them twinning: 1-3 blinks out at 0.3 then comes back to FULL by 0.5,
    // so it dies at its brightest and only the envelope takes it out; 1-4
    // gives a weak 0.3 echo and is already at 0 when its lifetime expires.
    {
      id: 'strand-1-3',
      enabled: true,
      start: [-0.25, -0.072, -0.29],
      end: [1.39, -0.145, -1.78],
      delay: 0.5,
      lifetime: 0.5,
      revealSeconds: 0.07,
      brightness: 5.2,
      thickness: 0.0021,
      jag: 0.03,
      detail: 22,
      flickerHz: 8.5,
      color: LIGHTNING_STRIKE_COLOR,
      innerColor: LIGHTNING_STRIKE_INNER_COLOR,
      keyframes: [
        { time: 0, intensity: 0.5, endOffset: [1.1, 1.215, -0.3] },
        { time: 0.25, intensity: 1, endOffset: [0.88, 1.265, -0.16] },
        { time: 0.3, intensity: 0, endOffset: [0.88, 1.265, -0.16] },
        { time: 0.45, intensity: 0.7, endOffset: [0.88, 1.265, -0.16] },
        { time: 0.5, intensity: 1, endOffset: [0.85, 1.315, -0.18] },
      ],
    },
    {
      id: 'strand-1-4',
      enabled: true,
      start: [-0.25, -0.112, -0.29],
      end: [0.66, -0.255, -1.17],
      delay: 0.55,
      lifetime: 0.5,
      revealSeconds: 0.07,
      brightness: 6.2,
      thickness: 0.0012,
      jag: 0.03,
      detail: 22,
      flickerHz: 8.5,
      color: LIGHTNING_STRIKE_COLOR,
      innerColor: LIGHTNING_STRIKE_INNER_COLOR,
      keyframes: [
        { time: 0, intensity: 0.5, endOffset: [1.1, 1.215, -0.3] },
        { time: 0.25, intensity: 1, endOffset: [0.88, 1.265, -0.16] },
        { time: 0.3, intensity: 0, endOffset: [0.88, 1.265, -0.16] },
        { time: 0.45, intensity: 0.3, endOffset: [0.88, 1.265, -0.16] },
        { time: 0.5, intensity: 0, endOffset: [0.88, 1.275, -0.18] },
      ],
    },
    // The tail: opens hardest (0.7) and is the only strand that simply fades
    // out instead of restriking, so the group ends rather than stops.
    {
      id: 'strand-1-5',
      enabled: true,
      start: [-0.36, -0.112, -0.29],
      end: [0.91, -0.175, -0.9],
      delay: 0.55,
      lifetime: 0.5,
      revealSeconds: 0.07,
      brightness: 6.2,
      thickness: 0.0021,
      jag: 0.03,
      detail: 22,
      flickerHz: 8.5,
      color: LIGHTNING_STRIKE_COLOR,
      innerColor: LIGHTNING_STRIKE_INNER_COLOR,
      keyframes: [
        { time: 0, intensity: 0.7, endOffset: [0.61, 1.215, -0.3] },
        { time: 0.3, intensity: 0, endOffset: [0.71, 1.165, -0.07] },
      ],
    },
  ],
};
